import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Send, BarChart3, Trophy, Zap, Lightbulb, Clock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveUserAvatar } from '../../utils/userAvatar';

const prompts = [
  { icon: Lightbulb, text: "Who's stuck on their current tasks?", query: "Who's stuck on their current tasks?" },
  { icon: Clock, text: "Where are we losing time to manual work?", query: "Where are we losing time to manual work?" },
  { icon: BarChart3, text: "Which projects have bottlenecks?", query: "Which projects have bottlenecks?" },
  { icon: Trophy, text: "Show me team productivity trends this week", query: "Show me team productivity trends this week" },
];

// Randomly pick one response from an array
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Simulated responses for demo — each query has multiple answers
const stuckResponses = [
  "I've detected 3 team members who may be stuck:\n\n⚠️ **Rahul Sharma** — Assigned to API Gateway task\n   • No commits in 3 days\n   • Last activity: opened a Stack Overflow tab\n   • Suggestion: Pair him with Michael on the integration\n\n⚠️ **Priya Patel** — Working on Auth Module\n   • Task estimate exceeded by 40%\n   • Multiple branch reverts detected\n   • Suggestion: Schedule a quick code review session\n\n⚠️ **David Kim** — Database Migration\n   • Marked 'In Progress' for 5 days\n   • Only 2 hours logged this week\n   • Suggestion: Check if he's waiting on DevOps access",

  "Here's who might need help right now:\n\n🔴 **Emily Johnson** — Dashboard Redesign\n   • Has been on the same subtask for 4 days\n   • Activity dropped 60% compared to last week\n   • She may need design feedback to move forward\n\n🟡 **Alex Martinez** — Payment Integration\n   • Opened 3 draft PRs but none finalized\n   • Spent 2.5 hrs in documentation today\n   • Likely blocked by unclear API specs\n\n🟢 **Sarah Chen** — Mobile App Testing\n   • Slightly behind schedule but active\n   • 5 bug reports filed today\n   • On track to finish by tomorrow",

  "Stuck team members detected this week:\n\n🚨 **Jason Lee** — seller7-backend\n   • Zero code pushes since Monday\n   • High meeting load: 4.5 hrs of meetings today\n   • Recommendation: Reduce his meetings and give focus time\n\n🚨 **Nina Gupta** — Frontend Refactor\n   • Same Jira ticket open for 6 days\n   • Frequent context switching (15 app switches/hr)\n   • Recommendation: Help her break the task into smaller pieces\n\n✅ Rest of the team is progressing well with an average of 78% task completion rate.",

  "Based on activity patterns, these team members appear blocked:\n\n⏸️ **Tom Wilson** — CI/CD Pipeline Setup\n   • Waiting on AWS credentials approval (3 days)\n   • Has been working on docs in the meantime\n   • Action: Escalate access request to DevOps lead\n\n⏸️ **Maria Garcia** — User Analytics Feature\n   • Blocked by data team's API changes\n   • Her last commit message: \"WIP - waiting for endpoint update\"\n   • Action: Sync with data team on timeline\n\n⏸️ **Kevin Brown** — Performance Optimization\n   • Profiling tools license expired\n   • No meaningful progress since Wednesday\n   • Action: Renew tooling subscription ASAP",
];

const manualWorkResponses = [
  "I've identified 3 major time sinks in manual work:\n\n1. **Excel Reporting** — 8.5 hrs/week across team\n   • Team members manually pulling data from 4 different sources\n   • Automation potential: High — could save 6+ hrs/week\n\n2. **Data Entry & CRM Updates** — 6.2 hrs/week\n   • Duplicate entry across Salesforce and internal tools\n   • Could integrate with CRM API to auto-sync\n\n3. **Status Update Meetings** — 4 hrs/week\n   • Daily standups running 30 min instead of 15\n   • Recommend async tools like TeamGrid Status Bot",

  "Here's where your team is losing time to repetitive work:\n\n📊 **Report Generation** — 7 hrs/week\n   • Sarah spends 2 hrs every Friday compiling sprint reports\n   • Michael manually creates client dashboards weekly\n   • Fix: Auto-generate reports from project tracking data\n\n📋 **Ticket Triage & Assignment** — 5.5 hrs/week\n   • Manual sorting of incoming support tickets\n   • Average 45 min/day per team lead\n   • Fix: Set up auto-routing rules based on keywords\n\n🔄 **Environment Setup** — 3 hrs/week\n   • New feature branches require manual config\n   • Fix: Dockerize dev environments",

  "Manual work hotspots detected across your team:\n\n🕐 **Timesheet Logging** — 5 hrs/week total\n   • Team members spend ~40 min/day on time entries\n   • Most entries are retroactive (filled at end of day)\n   • Solution: Auto-track from active window & Git commits\n\n🕐 **QA Testing (Manual)** — 9 hrs/week\n   • 60% of test cases are repetitive regression tests\n   • Only 25% of test suite is automated\n   • Solution: Increase automation coverage to 70%+\n\n🕐 **Code Review Coordination** — 3.5 hrs/week\n   • Developers pinging each other on Slack for reviews\n   • Average PR wait time: 6 hours\n   • Solution: Auto-assign reviewers with round-robin",

  "Top areas where automation could save your team 15+ hrs/week:\n\n⚡ **Invoice Processing** — 6 hrs/week\n   • Finance team manually matches POs with invoices\n   • Error rate: 8% requiring rework\n   • Automation ROI: Save 5 hrs + reduce errors by 90%\n\n⚡ **Deployment Checklist** — 4 hrs/week\n   • 12-step manual deployment process\n   • Average deploy time: 45 min\n   • CI/CD pipeline could cut this to 10 min\n\n⚡ **Meeting Notes & Action Items** — 5 hrs/week\n   • Manual note-taking in every meeting\n   • Action items often lost or forgotten\n   • AI meeting assistant could auto-capture & assign tasks",
];

const bottleneckResponses = [
  "I've found bottlenecks in 3 projects:\n\n🔴 **seller7-backend** — Critical\n   • Code review queue: 8 PRs waiting (avg wait: 2.3 days)\n   • Only 1 senior dev available for reviews\n   • Impact: Blocking 4 downstream tasks\n\n🟡 **Mobile App v2.0** — Moderate\n   • Design handoff delayed by 3 days\n   • 5 developers idle waiting for final mockups\n   • Impact: Sprint velocity down 35%\n\n🟡 **Analytics Dashboard** — Moderate\n   • Single point of failure: only Kevin knows the data pipeline\n   • He's on vacation next week\n   • Impact: Risk of complete stall",

  "Project bottleneck analysis:\n\n⚠️ **Frontend Redesign** — Blocked\n   • Waiting on API endpoints from backend team (ETA unknown)\n   • 3 frontend devs have been working on mock data for 4 days\n   • Recommendation: Backend team to prioritize API contract\n\n⚠️ **Payment Integration** — Slow Progress\n   • Compliance review taking longer than expected\n   • Legal team has 12 other reviews in queue\n   • Recommendation: Escalate priority with compliance team\n\n✅ **User Onboarding Flow** — Healthy\n   • All tasks on schedule\n   • Good team velocity: 42 story points/sprint\n   • No dependencies blocking progress",

  "Here are the major project bottlenecks this week:\n\n🚧 **Infrastructure Migration** — Severe\n   • Staging environment down since Tuesday\n   • 6 developers can't test their features\n   • DevOps team overwhelmed with 3 parallel migrations\n   • Fix: Prioritize staging recovery, defer non-critical migrations\n\n🚧 **Client Portal** — Moderate\n   • UX research results delayed — team designing without data\n   • Risk of rework if research contradicts current direction\n   • Fix: Use existing analytics as interim guidance\n\n🚧 **API v3 Launch** — Low but Growing\n   • Documentation not keeping up with development\n   • External partners can't start integration testing\n   • Fix: Assign dedicated technical writer this sprint",

  "Bottleneck report across active projects:\n\n📉 **E-commerce Platform** — Resource Bottleneck\n   • 2 critical bugs assigned to the same developer\n   • QA backlog: 14 tickets waiting for testing\n   • Database queries slowing down — needs optimization\n   • Action: Redistribute bugs, add QA support\n\n📉 **Marketing Automation** — Process Bottleneck\n   • Every feature requires 3 approval layers\n   • Average feature approval time: 5 days\n   • Developers spending 30% time on documentation for approvals\n   • Action: Streamline to single approval for small features\n\n✅ **Internal Tools** — Running Smoothly\n   • Consistent 90% sprint completion rate\n   • Clear ownership and minimal dependencies",
];

const productivityTrendsResponses = [
  "Here's your team's productivity trend for this week:\n\n📈 **Overall Score: 82%** (up 5% from last week)\n\n• **Monday:** 78% — Slow start, heavy meeting day\n• **Tuesday:** 85% — Peak productivity, deep work blocks\n• **Wednesday:** 84% — Strong momentum continued\n• **Thursday:** 80% — Slight dip due to sprint planning\n• **Friday:** 83% — Good finish to the week\n\n🏆 Top Performer: Sarah Rodriguez (92%)\n📊 Most Improved: Alex Martinez (+12% vs last week)\n⏰ Peak Hours: 9:30 AM – 11:30 AM across the team",

  "Weekly productivity insights:\n\n📊 **Team Average: 79%** (stable from last week)\n\n• **Deep Work Hours:** 24.5 hrs total (+3 hrs vs last week)\n• **Meeting Load:** 18% of total time (down from 22%)\n• **Context Switches:** Avg 8/hr (improved from 11/hr)\n• **Code Output:** 847 lines added, 312 removed\n\n👥 Team Breakdown:\n   • Engineering: 84% productivity\n   • Design: 76% productivity\n   • Product: 72% productivity (heavy meeting week)\n\n💡 Tip: Wednesday had the highest output — consider keeping Wednesdays meeting-free!",

  "This week's productivity dashboard:\n\n🎯 **Sprint Progress: 68%** (on track for 95% completion)\n\n• **Tasks Completed:** 34 out of 50\n• **Avg Task Completion Time:** 4.2 hrs (down from 5.1 hrs)\n• **Blockers Resolved:** 7 this week\n• **PRs Merged:** 23 (12% faster review cycle)\n\n📈 Trending Up:\n   • Focus time increased 15% after async standup adoption\n   • Pair programming sessions: 6 (team collaboration improving)\n\n📉 Watch Out:\n   • Friday afternoon productivity drops 40%\n   • 3 team members consistently work past 7 PM — burnout risk",

  "Productivity trends — Week of March 16-22:\n\n⚡ **Velocity: 38 story points** (vs 35 last week)\n\n• **Highest Output Day:** Tuesday — 9.2 productive hrs avg\n• **Lowest Output Day:** Monday — 5.8 productive hrs avg\n• **Collaboration Score:** 87% (team syncing well)\n• **Async vs Sync Ratio:** 3:1 (healthy balance)\n\n🔥 Highlights:\n   • Zero production incidents this week\n   • Feature delivery ahead of schedule by 1 day\n   • Code review turnaround improved to 4 hrs avg\n\n📋 Recommendations:\n   • Keep the Monday meeting load light — it's hurting momentum\n   • Thursday retro is working well, keep it\n   • Consider a Friday 'wrap-up' session to close open items",
];

// Simulated responses for demo
const getSimulatedResponse = (query) => {
  const lowerQuery = query.toLowerCase();

  if (lowerQuery.includes('stuck') || lowerQuery.includes('blocked')) {
    return pickRandom(stuckResponses);
  } else if (lowerQuery.includes('manual work') || lowerQuery.includes('losing time')) {
    return pickRandom(manualWorkResponses);
  } else if (lowerQuery.includes('bottleneck')) {
    return pickRandom(bottleneckResponses);
  } else if (lowerQuery.includes('productivity trend') || lowerQuery.includes('productivity trends')) {
    return pickRandom(productivityTrendsResponses);
  } else if (lowerQuery.includes('today') || lowerQuery.includes('team activity')) {
    return "Based on today's data, your team has been highly productive:\n\n• **Active Hours:** 6.5 hours across 8 team members\n• **Top Performer:** Sarah Rodriguez with 92% productivity score\n• **Most Active Project:** seller7-backend with 4.2 hours tracked\n• **Meeting Time:** 1.2 hours (18% of total time)\n\nYour team is performing above the weekly average!";
  } else if (lowerQuery.includes('top performer') || lowerQuery.includes('best')) {
    return "Here are this week's top performers:\n\n🏆 **1. Sarah Rodriguez** - 92% productivity\n   • 8.5 hours active time\n   • 12 deep work sessions\n\n🥈 **2. Michael Chen** - 88% productivity\n   • 7.8 hours active time\n   • 10 focus blocks completed\n\n🥉 **3. Emily Johnson** - 85% productivity\n   • 7.2 hours active time\n   • Strong consistency across projects";
  } else if (lowerQuery.includes('project') && (lowerQuery.includes('attention') || lowerQuery.includes('need'))) {
    return "Projects that need attention:\n\n⚠️ **seller7-backend** - Behind schedule\n   • Only 60% of estimated hours completed\n   • 2 team members inactive this week\n   • Recommendation: Schedule sync meeting\n\n⚠️ **Frontend Redesign** - Low activity\n   • 3 days without updates\n   • Recommendation: Check blockers\n\n✅ **API Integration** - On track\n   • 85% completion\n   • Active team engagement";
  } else if (lowerQuery.includes('efficiency') || lowerQuery.includes('improve') || lowerQuery.includes('optimize')) {
    return "Here are key recommendations to improve efficiency:\n\n💡 **Reduce Meeting Overload**\n   Your team spends 28% of time in meetings. Industry benchmark is 20%. Consider async status updates.\n\n💡 **Optimize Focus Blocks**\n   Top performers use 90-min focus blocks. Enable \"Focus Mode\" notifications for your team.\n\n💡 **Address Context Switching**\n   Engineering team averages 12 app switches/hour. Consider dedicated deep work windows.\n\n💡 **Schedule Optimization**\n   Productivity peaks between 9-11 AM. Schedule important work during these hours.";
  } else {
    return "I can help you understand your team's productivity patterns, identify bottlenecks, and suggest improvements. Try asking about:\n\n• Today's team activity\n• Top performers this week\n• Projects that need attention\n• Ways to improve efficiency\n\nWhat would you like to know?";
  }
};

const AskTeamGridAI = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const userAvatar = resolveUserAvatar(user);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Where are we losing time to manual work?",
      sender: 'user',
      timestamp: new Date(),
    },
    {
      id: 2,
      text: "I've identified 3 major time sinks:\n\n1. Excel Reporting\n   8.5 hrs/week across team\n   Automation potential: High\n\n2. Data Entry\n   6.2 hrs/week\n   Could integrate with CRM\n\n3. Status Updates\n   4 hrs/week\n   Recommend async tools",
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoClickIndex, setAutoClickIndex] = useState(0);
  const [activeAutoClickIndex, setActiveAutoClickIndex] = useState(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatAreaRef = useRef(null);
  const autoClickIntervalRef = useRef(null);
  const placeholderIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const placeholders = [
    "Ask anything about your team...",
    "Who's stuck on their current tasks?",
    "Where are we losing time to manual work?",
    "Which projects have bottlenecks?",
    "Show me team productivity trends this week",
    "What did the team work on today?",
    "Who are the top performers this week?",
  ];

  // Handle prompt function
  const handlePrompt = useCallback(async (query, isManualClick = false, clickedIndex = null) => {
    if (isLoading) return;

    // Keep the clicked button highlighted
    if (isManualClick && clickedIndex !== null) {
      setActiveAutoClickIndex(clickedIndex);
      setAutoClickIndex(clickedIndex);
    }
    
    setInputValue('');
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      text: query,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Simulate AI thinking delay (short delay for UX)
    await new Promise(resolve => setTimeout(resolve, 300));

    // Get simulated response
    const responseText = getSimulatedResponse(query);
    const botMessageId = Date.now() + 1;
    
    // Create bot message with full text immediately (no streaming)
    const botMessage = {
      id: botMessageId,
      text: responseText,
      sender: 'ai',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, botMessage]);
    
    setIsLoading(false);
    
    // Scroll to bottom after message is added (scroll chat area only, not the page)
    setTimeout(() => {
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTo({
          top: chatAreaRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 150);
  }, [isLoading]);

  // Auto-scroll to bottom when new messages arrive (only if there are messages)
  useEffect(() => {
    if (chatAreaRef.current && messages.length > 0) {
      // Scroll the chat area container directly instead of using scrollIntoView
      // This prevents the entire page from scrolling
      const timer = setTimeout(() => {
        if (chatAreaRef.current) {
          // Use smooth scroll behavior on the container itself
          chatAreaRef.current.scrollTo({
            top: chatAreaRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  // Auto-click prompts every 5 seconds
  useEffect(() => {
    // Clear any existing interval
    if (autoClickIntervalRef.current) {
      clearInterval(autoClickIntervalRef.current);
    }

    // Set up auto-click interval
    autoClickIntervalRef.current = setInterval(() => {
      if (!isLoading) {
        setAutoClickIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % prompts.length;
          
          // Highlight the button being auto-clicked (stays highlighted until next click)
          setActiveAutoClickIndex(nextIndex);

          // Auto-click the next prompt
          handlePrompt(prompts[nextIndex].query);
          return nextIndex;
        });
      }
    }, 12000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (autoClickIntervalRef.current) {
        clearInterval(autoClickIntervalRef.current);
      }
    };
  }, [isLoading, handlePrompt]); // Re-run when loading state or handlePrompt changes

  // Typing animation for placeholder
  useEffect(() => {
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (inputValue.trim() || isLoading) {
      setTypedPlaceholder('');
      setIsTyping(false);
      return;
    }

    const currentPlaceholder = placeholders[placeholderIndex];
    const currentIndexRef = { value: 0 };
    setIsTyping(true);
    setTypedPlaceholder('');

    const typeChar = () => {
      if (inputValue.trim() || isLoading) {
        setTypedPlaceholder('');
        setIsTyping(false);
        return;
      }

      if (currentIndexRef.value < currentPlaceholder.length) {
        setTypedPlaceholder(currentPlaceholder.slice(0, currentIndexRef.value + 1));
        currentIndexRef.value++;
        typingTimeoutRef.current = setTimeout(typeChar, 50); // 50ms per character
      } else {
        setIsTyping(false);
        // Wait a bit before changing to next placeholder
        typingTimeoutRef.current = setTimeout(() => {
          if (!inputValue.trim() && !isLoading) {
            setPlaceholderIndex((prevIndex) => (prevIndex + 1) % placeholders.length);
          }
        }, 2000); // Wait 2 seconds after typing completes
      }
    };

    // Start typing after a short delay
    typingTimeoutRef.current = setTimeout(() => {
      if (!inputValue.trim() && !isLoading) {
        typeChar();
      }
    }, 500);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [placeholderIndex, inputValue, isLoading]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const query = inputValue.trim();
    setInputValue('');
    await handlePrompt(query);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="relative">
      {/* Main window */}
      <div 
        className={`relative rounded-3xl overflow-hidden flex flex-col ${
          isDark 
            ? 'bg-[#0a0a0a] border border-white/10' 
            : 'bg-white border border-gray-200'
        }`}
        style={{
          boxShadow: isDark
            ? '0 0 0 1px rgba(40, 180, 174, 0.05), 0 40px 100px -20px rgba(0, 0, 0, 0.7)'
            : '0 0 0 1px rgba(40, 180, 174, 0.05), 0 40px 100px -20px rgba(0, 0, 0, 0.1)',
          height: '650px',
          maxHeight: '650px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div className={`flex items-center gap-3 px-6 py-4 ${
          isDark 
            ? 'bg-black/40 border-b border-white/5' 
            : 'bg-gray-50/50 border-b border-gray-200'
        }`}>
          {/* Green dot */}
          <span 
            className="w-2 h-2 rounded-full"
            style={{ background: '#4CAF50' }}
          />
          
          {/* Title */}
          <div className="flex-1 flex items-center gap-2">
            <h3 className={`text-base font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Ask TeamGrid AI
            </h3>
            <span 
              className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider text-white"
              style={{
                background: '#4CAF50',
              }}
            >
              LIVE
            </span>
          </div>
        </div>

        {/* Chat area */}
        <div 
          ref={chatAreaRef}
          className="flex-1 p-6 overflow-y-auto min-h-0 chat-scroll-area"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(40, 180, 174, 0.4) rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.2)',
            maxHeight: '100%',
            overflow: 'auto',
          }}
        >
          <style dangerouslySetInnerHTML={{__html: `
            .chat-scroll-area::-webkit-scrollbar {
              width: 8px;
            }
            .chat-scroll-area::-webkit-scrollbar-track {
              background: ${isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
              border-radius: 4px;
            }
            .chat-scroll-area::-webkit-scrollbar-thumb {
              background: ${isDark ? 'rgba(40, 180, 174, 0.4)' : 'rgba(0, 0, 0, 0.4)'};
              border-radius: 4px;
            }
            .chat-scroll-area::-webkit-scrollbar-thumb:hover {
              background: ${isDark ? 'rgba(40, 180, 174, 0.6)' : 'rgba(0, 0, 0, 0.6)'};
            }
          `}} />
          <div className="flex flex-col gap-5">

            <AnimatePresence>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className={`flex items-end gap-2 ${
                    message.sender === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  {/* Avatar */}
                  {message.sender === 'user' && userAvatar ? (
                    <img src={userAvatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                      message.sender === 'user'
                        ? 'bg-[#E0CCF2] text-[#232323]'
                        : 'bg-[#28B4AE] text-white'
                    }`}>
                      {message.sender === 'user' ? 'You' : 'AI'}
                    </div>
                  )}
                  
                  {/* Message bubble */}
                  <div className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    message.sender === 'user' 
                      ? 'bg-[#E0CCF2] text-[#232323]' 
                      : isDark 
                        ? 'bg-[#1A1A1A] text-white' 
                        : 'bg-white text-[#232323] border border-gray-200'
                  }`}>
                    {message.text.split('\n').map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line.split(/(\*\*.*?\*\*)/).map((part, pIdx) =>
                          part.startsWith('**') && part.endsWith('**')
                            ? <strong key={pIdx}>{part.slice(2, -2)}</strong>
                            : part
                        )}
                        {idx < message.text.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-end gap-2"
              >
                <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-[#28B4AE] text-white">
                  AI
                </div>
                <div className={`px-5 py-4 rounded-2xl rounded-bl-sm flex gap-1.5 ${
                  isDark ? 'bg-[#1A1A1A]' : 'bg-white border border-gray-200'
                }`}>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full bg-[#28B4AE] animate-bounce"
                      style={{
                        animationDelay: `${i * 0.2}s`,
                        animationDuration: '1.4s',
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Prompts */}
        <div className={`px-6 py-3 ${
          isDark 
            ? 'bg-black/25 border-t border-white/5' 
            : 'bg-gray-50/50 border-t border-gray-200'
        }`}>
          <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: '#AAAAAA' }}>
            TRY ASKING
          </div>
          <div className="grid grid-cols-2 gap-2">
            {prompts.map((prompt, index) => {
              const IconComponent = prompt.icon;
              let iconColor = '#FFC107'; // Default yellow/orange for lightbulb
              if (prompt.icon === BarChart3) {
                // Bar chart - will render custom bars
                iconColor = null;
              } else if (prompt.icon === Trophy) {
                iconColor = '#FFD700'; // Golden yellow for trophy
              } else if (prompt.icon === Clock) {
                iconColor = '#9CA3AF'; // Gray for clock
              }
              
              const isAutoClicking = activeAutoClickIndex === index;
              
              return (
                <button
                  key={index}
                  onClick={() => handlePrompt(prompt.query, true, index)}
                  disabled={isLoading}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left cursor-pointer ${
                    isDark ? 'text-white' : 'text-[#232323]'
                  }`}
                  style={{
                    background: isAutoClicking
                      ? (isDark
                          ? 'linear-gradient(135deg, rgba(40, 180, 174, 0.15), rgba(40, 180, 174, 0.05))'
                          : 'linear-gradient(135deg, rgba(40, 180, 174, 0.12), rgba(40, 180, 174, 0.04))')
                      : (isDark ? '#1A1A1A' : '#F0F0F0'),
                    border: isAutoClicking
                      ? '1.5px solid rgba(40, 180, 174, 0.5)'
                      : '1.5px solid transparent',
                    boxShadow: isAutoClicking
                      ? (isDark
                          ? '0 0 12px rgba(40, 180, 174, 0.15), inset 0 1px 0 rgba(40, 180, 174, 0.1)'
                          : '0 0 12px rgba(40, 180, 174, 0.1), inset 0 1px 0 rgba(40, 180, 174, 0.08)')
                      : 'none',
                    transition: 'all 0.3s ease',
                    ...(isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && !isAutoClicking) {
                      e.currentTarget.style.background = isDark ? '#1F1F1F' : '#E0E0E0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading && !isAutoClicking) {
                      e.currentTarget.style.background = isDark ? '#1A1A1A' : '#F0F0F0';
                    }
                  }}
                >
                  {prompt.icon === BarChart3 ? (
                    <div className="flex items-end gap-0.5 h-4">
                      <div className="w-1.5 bg-green-500 h-3" />
                      <div className="w-1.5 bg-red-500 h-4" />
                      <div className="w-1.5 bg-blue-500 h-2.5" />
                    </div>
                  ) : (
                    <IconComponent className="w-4 h-4" style={{ color: iconColor }} />
                  )}
                  <span>{prompt.text}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Input */}
        <div className={`px-6 py-4 pb-5 ${
          isDark 
            ? 'bg-black/30 border-t border-white/5' 
            : 'bg-gray-50/50 border-t border-gray-200'
        }`}>
          <div
            className="flex gap-3 px-3.5 py-1.5 rounded-xl transition-all"
            style={{
              background: isDark ? '#1A1A1A' : '#F0F0F0',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="flex-1 relative">
              {!inputValue && (
                <div 
                  className={`absolute left-0 top-0 pointer-events-none text-sm flex items-center h-full ${
                    isDark ? 'text-[#A1A1AA]' : 'text-[#AAAAAA]'
                  }`}
                >
                  <span>{typedPlaceholder}</span>
                  {isTyping && (
                    <span 
                      className={`ml-0.5 inline-block w-0.5 h-4 animate-pulse ${
                        isDark ? 'bg-[#A1A1AA]' : 'bg-[#AAAAAA]'
                      }`}
                      style={{
                        animation: 'blink 1s infinite',
                      }}
                    />
                  )}
                  <style>{`
                    @keyframes blink {
                      0%, 50% { opacity: 1; }
                      51%, 100% { opacity: 0; }
                    }
                  `}</style>
                </div>
              )}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder=""
                disabled={isLoading}
                className={`w-full bg-transparent border-none outline-none text-sm relative z-10 ${
                  isDark ? 'text-white' : 'text-[#232323]'
                }`}
              />
            </div>
            <button
              type="button"
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all bg-transparent ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'
              }`}
            >
              <Mic className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#232323]'}`} />
            </button>
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all bg-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'
              }`}
            >
              <Send className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#232323]'}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskTeamGridAI;
